import React from 'react';
import {
  Lock, CreditCard, ArrowRight, AlertCircle, CheckCircle,
  Home, Shield, Clock, X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface CheckoutSidebarProps {
  price: string;
  title: string;
  isLoading: boolean;
  error: string | null;
  onCheckout: () => void;
  icon?: LucideIcon;
}

export function CheckoutSidebar({ price, title, isLoading, error, onCheckout, icon }: CheckoutSidebarProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  return (
    <>
      <div className="hidden lg:block">
        <div className="sticky top-8 space-y-5">
          <SidebarCard
            price={price}
            title={title}
            isLoading={isLoading}
            error={error}
            onCheckout={onCheckout}
            icon={icon}
          />
          <TrustBadges />
          <QualityGuarantee />
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
        {mobileOpen && (
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} />
        )}

        {mobileOpen && (
          <div className="relative z-50 bg-white rounded-t-3xl shadow-2xl border-t border-gray-200 p-6 animate-slide-down">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
            <SidebarCard
              price={price}
              title={title}
              isLoading={isLoading}
              error={error}
              onCheckout={onCheckout}
            />
          </div>
        )}

        {!mobileOpen && (
          <div className="bg-white border-t border-gray-200 px-4 py-3 shadow-2xl">
            <div className="flex items-center justify-between gap-3 max-w-lg mx-auto">
              <div>
                <p className="text-lg font-bold text-gray-900">&pound;{price}</p>
                <p className="text-xs text-gray-500">One-time payment</p>
              </div>
              <button
                onClick={() => {
                  if (mobileOpen) {
                    onCheckout();
                  } else {
                    setMobileOpen(true);
                  }
                }}
                className="flex-1 max-w-[220px] bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200"
              >
                Pay &pound;{price}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function SidebarCard({ price, title, isLoading, error, onCheckout, icon }: CheckoutSidebarProps) {
  const IconComponent = icon || Home;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-elevated">
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-white/80" />
            <span className="text-white/90 text-xs font-semibold uppercase tracking-wider">Secure Checkout</span>
          </div>
          <CreditCard className="w-4 h-4 text-white/60" />
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-5">Order Summary</h3>

        <div className="flex items-center gap-3 pb-5 border-b border-gray-100">
          <div className="w-11 h-11 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
            <IconComponent className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
            <p className="text-xs text-gray-500">One-time payment</p>
          </div>
        </div>

        <div className="py-4 space-y-2.5 border-b border-dashed border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Service fee</span>
            <span className="text-sm text-gray-900 font-medium">&pound;{price}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">VAT</span>
            <span className="text-sm text-gray-400">Included</span>
          </div>
        </div>

        <div className="flex items-center justify-between py-5 mb-5">
          <span className="text-base font-bold text-gray-900">Total</span>
          <span className="text-3xl font-bold text-gray-900">&pound;{price}</span>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <button
          onClick={onCheckout}
          disabled={isLoading}
          className="group w-full py-4 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
        >
          <span className="absolute inset-0 bg-shimmer animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity" />
          <span className="relative inline-flex items-center gap-2">
            {isLoading ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Redirecting to payment...
              </>
            ) : (
              <>
                Pay &pound;{price}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </span>
        </button>

        <p className="text-xs text-gray-400 text-center mt-3">
          Secure payment processed by Stripe
        </p>
      </div>
    </div>
  );
}

function TrustBadges() {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-elevated">
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Lock className="w-4 h-4 text-gray-600" />
          </div>
          <p className="text-xs font-medium text-gray-700">256-bit SSL</p>
          <p className="text-[10px] text-gray-400">Encryption</p>
        </div>
        <div className="text-center">
          <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Shield className="w-4 h-4 text-gray-600" />
          </div>
          <p className="text-xs font-medium text-gray-700">PCI DSS</p>
          <p className="text-[10px] text-gray-400">Compliant</p>
        </div>
        <div className="text-center">
          <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Clock className="w-4 h-4 text-gray-600" />
          </div>
          <p className="text-xs font-medium text-gray-700">Instant</p>
          <p className="text-[10px] text-gray-400">Delivery</p>
        </div>
      </div>
    </div>
  );
}

function QualityGuarantee() {
  return (
    <div className="rounded-2xl border p-4 bg-gray-50 border-gray-200">
      <div className="flex items-start gap-3">
        <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-900" />
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-0.5">Quality Guarantee</p>
          <p className="text-xs text-gray-600 leading-relaxed">
            Every document is reviewed against the latest legislation with detailed annotations explaining every change made.
          </p>
        </div>
      </div>
    </div>
  );
}
